import React from 'react';
import styled from 'styled-components';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ModalImage = styled.img`
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const CloseButton = styled.button`
  position: absolute;
  top: -50px;
  right: 0;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  font-size: 24px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }
`;

const ImageInfo = styled.div`
  margin-top: 20px;
  color: white;
  text-align: center;
  max-width: 600px;
`;

const ImageTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const ImageDescription = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.5;
`;

const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  font-size: 24px;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-50%) scale(1.1);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const PrevButton = styled(NavigationButton)`
  left: -80px;
`;

const NextButton = styled(NavigationButton)`
  right: -80px;
`;

function ImageModal({ 
  isOpen, 
  onClose, 
  imageUrl, 
  images = [], 
  currentIndex = 0, 
  onPrevious, 
  onNext,
  title,
  description 
}) {
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowLeft' && onPrevious) {
      onPrevious();
    } else if (e.key === 'ArrowRight' && onNext) {
      onNext();
    }
  };

  React.useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen, onClose, onPrevious, onNext]);

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContent>
        <CloseButton onClick={onClose}>
          ×
        </CloseButton>
        
        {images.length > 1 && onPrevious && (
          <PrevButton 
            onClick={onPrevious}
            disabled={currentIndex === 0}
          >
            ‹
          </PrevButton>
        )}
        
        <ModalImage 
          src={imageUrl} 
          alt={title || '图片预览'}
          onError={(e) => {
            e.target.src = 'https://picsum.photos/800/600?random=' + Date.now();
          }}
        />
        
        {images.length > 1 && onNext && (
          <NextButton 
            onClick={onNext}
            disabled={currentIndex === images.length - 1}
          >
            ›
          </NextButton>
        )}
        
        {(title || description) && (
          <ImageInfo>
            {title && <ImageTitle>{title}</ImageTitle>}
            {description && <ImageDescription>{description}</ImageDescription>}
          </ImageInfo>
        )}
      </ModalContent>
    </ModalOverlay>
  );
}

export default ImageModal;